import {
  Chapter,
  ChapterFunctionary,
  DistrictMember,
  EventType,
  Functionary,
  IamUser,
  Member,
  OrganizationChart,
  UserManagmentItem,
  UserPermission,
} from "../types/apiTypes";
import {
  DocumentFile,
  PostDocumentFileBody,
  PostPolicy,
  Result,
} from "../types/types";

const baseUrl = "/api";

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

interface DownloadUrlResponse {
  downloadUrl: string;
  expiresAt: string;
}

export const getUsers = async () => {
  return await getData<UserManagmentItem[]>(`${baseUrl}/v1/users`);
};

export const getEvents = async (year: number, month: number) => {
  const params = new URLSearchParams({
    year: String(year),
    month: String(month),
  });
  return await getData<EventType[]>(
    `${baseUrl}/v1/events?${params.toString()}`,
  );
};

export const getFunctionaries = async () => {
  return await getData<Functionary[]>(`${baseUrl}/v1/functionaries`);
};

export const getOrganizationChart = async () => {
  return await getData<OrganizationChart>(
    `${baseUrl}/v1/functionaries/organization_chart`,
  );
};

export const getChapters = async () => {
  return await getData<Chapter[]>(`${baseUrl}/v1/chapters`);
};

export const getChapterMembers = async (id: number) => {
  return await getData<Member[]>(`${baseUrl}/v1/chapters/${id}/members`);
};

export const getChapterFunctionaries = async (id: number) => {
  return await getData<ChapterFunctionary[]>(
    `${baseUrl}/v1/chapters/${id}/functionaries`,
  );
};

export const getPermissions = async (): Promise<UserPermission | null> => {
  return (await getData<UserPermission>(`${baseUrl}/v1/permissions`)) ?? null;
};

export const postUserPermission = async (item: UserManagmentItem) => {
  return await postData(`${baseUrl}/v1/users/${item.iamUser?.id}/permission`, {
    userId: item.iamUser?.id,
    personId: item.person?.personId,
    permission: item.permission,
  });
};

export const deleteUserPermission = async (id: string) => {
  return await doDelete(`${baseUrl}/v1/users/${id}/permission`);
};

export const getIAMUsers = async (
  query?: string,
): Promise<IamUser[] | undefined> => {
  return await getData<IamUser[]>(
    `${baseUrl}/v1/iam/users${query ? `?query=${query}` : ""}`,
  );
};

export const getPersonPicture = async (id: string) => {
  return await getBlob(`${baseUrl}/v1/functionaries/${id}/picture`);
};

export const getTemplates = async () => {
  return await getData<string[]>(`${baseUrl}/v1/templates`);
};

export const getDistrictsCommitteeList = async (id: number) => {
  return await getData<DistrictMember[]>(
    `${baseUrl}/v1/districts/${id}/committee`,
  );
};

export const getTemplateFile = async (
  chaperId: number | undefined,
  filename: string,
) => {
  return await getBlob(`${baseUrl}/v1/templates/${chaperId}/${filename}`);
};

export const getLogo = async (chapterId: number | undefined) => {
  return await getBlob(`${baseUrl}/v1/chapters/${chapterId}/logo`);
};

export const getLogos = async (chapter: Chapter | undefined) => {
  const logo = await getLogo(chapter?.id);

  if (!logo) return;

  return ["Logo.png"];
};

export const getCommitteeExport = async (chapterId: number) => {
  return await getBlob(`${baseUrl}/v1/chapters/${chapterId}/functionaries`, {
    Accept: "text/csv",
  });
};

export const getMemberExport = async (chapterId: number) => {
  return await getBlob(`${baseUrl}/v1/chapters/${chapterId}/members`, {
    Accept: "text/csv",
  });
};

export const getFiles = async (chapterId: number) => {
  const res = await getData<PaginatedResponse<DocumentFile>>(
    `${baseUrl}/v1/chapters/${chapterId}/files`,
  );
  return res?.items ?? [];
};

export const upsertFile = async (
  chapterId: number,
  body: PostDocumentFileBody,
) => {
  return (
    await postData(`${baseUrl}/v1/chapters/${chapterId}/files`, body)
  ).json() as Promise<DocumentFile>;
};

export const postFileUploadURL = async (
  chapterId: number,
  fileName: string,
): Promise<PostPolicy> => {
  return await postPolicyData<PostPolicy>(
    `${baseUrl}/v1/chapters/${chapterId}/files/create_file_upload_url`,
    { name: fileName },
  );
};

export const postFileDownloadURL = async (
  chapterId: number,
  fileName: string,
): Promise<DownloadUrlResponse> => {
  return await postPolicyData<DownloadUrlResponse>(
    `${baseUrl}/v1/chapters/${chapterId}/files/create_file_download_url`,
    { name: fileName },
  );
};

export const deleteFile = async (chapterId: number, fileId: number) => {
  return await doDelete(`${baseUrl}/v1/chapters/${chapterId}/files/${fileId}`);
};

const getData = async <Response>(
  url: string,
): Promise<Response | undefined> => {
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  if (response.status !== 200) return;
  return await response.json();
};

const postData = (url: string, data?: object): Promise<Response> => {
  return fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

const postPolicyData = async <T>(url: string, data?: object): Promise<T> => {
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  return response.json() as Promise<T>;
};

const doDelete = async (url: string): Promise<Response | undefined> => {
  return await fetch(url, {
    method: "Delete",
    credentials: "include",
  });
};

const getBlob = async (
  url: string,
  headers?: Record<string, string>,
): Promise<Blob | undefined> => {
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      ...headers,
    },
  });
  if (response.status !== 200) return;
  return await response.blob();
};

export const uploadFile = async (
  policy: PostPolicy,
  _: AbortSignal,
  file: File,
): Promise<Result<boolean | null>> => {
  const formData = new FormData();

  for (const field in policy.fields) {
    const value = policy.fields[field];
    value && formData.append(field, value);
  }
  // Must be the last child of the form
  // https://stackoverflow.com/questions/16378713/unable-to-upload-file-to-google-cloud-storage-using-post-method
  formData.append("file", file);

  try {
    await fetch(policy.url, {
      method: "POST",
      body: formData,
    });
    return {
      data: true,
      error: null,
    };
  } catch (err) {
    return {
      data: null,
      error: {
        code: 500,
        message: "Unknown error",
      },
    };
  }
};
